import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'expo-router';
import { paymentErrorType } from 'features/payment/types';
import { useStoreContext } from 'features/store';
import { useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import { logCartPlaceOrder } from 'experiments/checkout-v2/treatment/pages/cart/payment/utils';
import { IUseCommitOrderParams } from 'hooks/commit-order/types';
import { useCommitOrder } from 'hooks/commit-order/use-commit-order';
import { useCart } from 'hooks/use-cart';
import useErrorModal from 'hooks/use-error-modal';
import { useIOSLiveActivities } from 'hooks/use-ios-live-activities';
import { useHandleCommitError } from 'pages/cart/hooks/use-handle-commit-error';
import { useCartContext } from 'state/cart';
import { actions, useAppDispatch } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

import { useCheckoutContext } from '../use-checkout-context';

import { useBuildCommitOrderPayload } from './use-build-commit-order-payload';
import { useRequestPaymentDetails } from './use-get-payment-details';
import { useLoyaltyDiscounts } from './use-loyalty-discounts';

export const useCheckoutSubmit = () => {
  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);

  const router = useRouter();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const { store } = useStoreContext();
  const { emptyCart } = useCart();
  const { startActivity: startLiveActivity } = useIOSLiveActivities();
  const {
    totals,
    paymentValues,
    isPaymentType,
    isPaymentProcessor,
    isLoadingPaymentMethods,
    serverOrder,
    distanceToStore,
    checkoutPaymentMethodId,
    setCheckoutState,
    isSubmittingOrder,
  } = useCheckoutContext();
  const { subTotalCents = 0, taxCents = 0 } = totals || {};

  const { isOrderPriceLoading } = useCartContext();

  const {
    data: paymentDetails,
    isLoading: isRequestingPaymentDetails,
    error: paymentDetailsError,
    requestPayment: requestPaymentDetails,
  } = useRequestPaymentDetails();

  const {
    buildCommitOrderPayloadWithAccount,
    buildCommitOrderPayloadWithoutAccount,
  } = useBuildCommitOrderPayload();

  const { rewardDiscountAmount, additionalDiscountAmount } = useLoyaltyDiscounts();
  const isOneTimeCardPayment = Boolean(
    paymentValues && !paymentValues.saveCard && paymentValues.cardNumber
  );
  const [shouldDisplayErrorDialog, setShouldDisplayErrorDialog] = useState(false);
  const [wasCommitOrderCalled, setWasCommitOrderCalled] = useState(false);

  const commitOrderParams: IUseCommitOrderParams = useMemo(
    () => ({
      rbiOrderId: serverOrder && serverOrder.rbiOrderId,
      distanceToStore,
      checkoutData: {
        savings: additionalDiscountAmount,
        rewardsSaving: rewardDiscountAmount,
        tax: taxCents,
        total: subTotalCents + taxCents,
        subtotal: subTotalCents,
      },
    }),
    [
      serverOrder,
      distanceToStore,
      additionalDiscountAmount,
      rewardDiscountAmount,
      taxCents,
      subTotalCents,
    ]
  );

  const {
    commitOrder,
    forceCommitOrder,
    // TODO: The commit error result and the commit order failure should be unified and returned as a single result.
    // https://rbictg.atlassian.net/browse/BKPE-22766
    commitResult: { error: commitError },
    loading: isCommitOrderLoading,
    success: commitOrderSuccess,
    failure: commitOrderFailure,
  } = useCommitOrder(commitOrderParams);

  const commitOrderError = commitError || commitOrderFailure;

  const shouldCommitOrder = [
    OrderInjectionFlowVariations.INJECT_NOW,
    OrderInjectionFlowVariations.INJECT_AND_HOLD,
    OrderInjectionFlowVariations.TIMED_FIRE,
  ].includes(orderInjectFlow as OrderInjectionFlowVariations);

  const logContext = useMemo(
    () => ({
      isOneTimeCardPayment,
      isPaymentType,
      ...commitOrderParams,
    }),
    [isOneTimeCardPayment, isPaymentType, commitOrderParams]
  );

  const navigateToOrderConfirmation = useCallback(
    (completedOrder: IServerOrder) => {
      router.replace(`${routes.orderConfirmation}/${completedOrder.rbiOrderId}?showToast=true`);
    },
    [router]
  );

  const [ErrorDialog, openErrorDialog, dismissErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Submit Order Error',
  });

  const handleRequestPaymentDetailsError = useCallback(() => {
    if (!shouldDisplayErrorDialog) {
      return;
    }

    logger.error({
      message: '[useCheckoutSubmit][submitOrder]: Error requesting payment details',
      paymentDetailsError,
      ...logContext,
    });
    let message = '';
    switch (paymentDetailsError) {
      case paymentErrorType.applePayGeneric:
        message = formatMessage({ id: 'orderFailureMessageForApplePay' });
        break;
      case paymentErrorType.googlePayGeneric:
        message = formatMessage({ id: 'orderFailureMessageForGooglePay' });
        break;
      case paymentErrorType.cashAppGeneric:
        message = formatMessage({ id: 'cashAppError' });
        break;
      case paymentErrorType.cashAppDeclined:
        message = formatMessage({ id: 'cashAppDecline' });
        break;
      default:
        message = formatMessage({ id: 'pspPaymentDeclinedTransactionForterTitle' });
    }
    openErrorDialog({ message });
    setShouldDisplayErrorDialog(false);
  }, [formatMessage, logContext, openErrorDialog, paymentDetailsError, shouldDisplayErrorDialog]);

  useHandleCommitError({
    fdAccountId: checkoutPaymentMethodId ?? '',
    openErrorDialog,
    dismissErrorDialog,
    onCoolingPeriodConfirm: forceCommitOrder,
    commitError,
  });

  // Handles the loading state of the order submission
  useEffect(() => {
    const isCurrentlySubmitting =
      isOrderPriceLoading ||
      isRequestingPaymentDetails ||
      isLoadingPaymentMethods ||
      isCommitOrderLoading ||
      (wasCommitOrderCalled && !commitOrderSuccess && !commitOrderError);

    setCheckoutState(prev => ({
      ...prev,
      isSubmittingOrder: isCurrentlySubmitting,
    }));
  }, [
    isOrderPriceLoading,
    isRequestingPaymentDetails,
    isLoadingPaymentMethods,
    isCommitOrderLoading,
    wasCommitOrderCalled,
    commitOrderSuccess,
    setCheckoutState,
    commitOrderError,
  ]);

  // Handles order submission
  useEffect(() => {
    if (isOrderPriceLoading || !serverOrder) {
      return;
    }
    if (commitOrderSuccess) {
      emptyCart();
      // Start IOS Live Activity Widget
      startLiveActivity(serverOrder);

      navigateToOrderConfirmation(serverOrder);
    }

    if (commitOrderError) {
      if (serverOrder?.orderErrors && !commitError && wasCommitOrderCalled) {
        setWasCommitOrderCalled(false);
        openErrorDialog({ message: formatMessage({ id: 'orderFailureMessage' }) });
        return;
      }
      setWasCommitOrderCalled(false);
    }
  }, [
    store.number,
    serverOrder,
    isOrderPriceLoading,
    commitOrderSuccess,
    commitOrderError,
    emptyCart,
    startLiveActivity,
    navigateToOrderConfirmation,
    commitError,
    openErrorDialog,
    formatMessage,
    wasCommitOrderCalled,
  ]);

  // Handles the trigger of the commit order when the payment details are available
  useEffect(() => {
    if (isRequestingPaymentDetails || wasCommitOrderCalled || commitOrderError) {
      return;
    }
    async function handleCommitOrder() {
      setCheckoutState(prev => ({
        ...prev,
        isBuildingCommitOrderPayload: true,
      }));

      try {
        const payload = await buildCommitOrderPayloadWithoutAccount(paymentDetails);
        if (!payload) {
          logger.error({
            message:
              '[useCheckoutSubmit][submitOrder]: Empty order payload from buildCommitOrderPayloadWithoutAccount',
            ...logContext,
          });
          return;
        }
        commitOrder(payload);
      } finally {
        setCheckoutState(prev => ({
          ...prev,
          isBuildingCommitOrderPayload: false,
        }));
      }
    }
    if (!paymentDetailsError && paymentDetails) {
      setWasCommitOrderCalled(true);
      handleCommitOrder();
    }

    if (paymentDetailsError) {
      handleRequestPaymentDetailsError();
    }
  }, [
    paymentDetails,
    isRequestingPaymentDetails,
    buildCommitOrderPayloadWithoutAccount,
    commitOrder,
    paymentDetailsError,
    logContext,
    handleRequestPaymentDetailsError,
    isCommitOrderLoading,
    isSubmittingOrder,
    commitOrderSuccess,
    commitError,
    wasCommitOrderCalled,
    setCheckoutState,
    commitOrderError,
  ]);

  const submitOrder = async () => {
    setShouldDisplayErrorDialog(true);
    if (!shouldCommitOrder) {
      logger.warn({
        message: '[useCheckoutSubmit][submitOrder]: Skipping order commit',
        shouldCommitOrder,
        ...logContext,
      });
      return;
    }
    logCartPlaceOrder();
    dispatch(actions.loyalty.setShouldRefetchOffers(true));
    if (
      isOneTimeCardPayment ||
      isPaymentProcessor.braintree ||
      isPaymentType.cashApp ||
      isPaymentType.googlePay ||
      isPaymentType.applePay
    ) {
      // This method asynchronously fetches the payment details.
      // Once paymentDetails is updated, an effect (defined above) will trigger commitOrder.
      // This ensures that the order is only committed after the latest payment information is available.
      requestPaymentDetails();
    } else {
      setCheckoutState(prev => ({
        ...prev,
        isBuildingCommitOrderPayload: true,
      }));
      const payload = await buildCommitOrderPayloadWithAccount();
      if (!payload) {
        logger.error({
          message:
            '[useCheckoutSubmit][submitOrder]: Empty order payload from buildCommitOrderPayloadWithAccount',
          ...logContext,
        });
        setCheckoutState(prev => ({
          ...prev,
          isBuildingCommitOrderPayload: false,
        }));
        return;
      }
      setCheckoutState(prev => ({
        ...prev,
        isBuildingCommitOrderPayload: false,
      }));
      setWasCommitOrderCalled(true);
      commitOrder(payload);
    }
  };

  return {
    navigateToOrderConfirmation,
    submitOrder,
    isCommitOrderLoading,
    ErrorDialog,
    wasCommitOrderCalled,
    commitOrderSuccess,
    commitOrderError,
  };
};
